import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { SovosBox, SovosTypography } from "mosaic-react";
import { isCellMismatchResolved } from "../utils";
import AiFixPanel from "./components/AiFixPanel";
import IssuesSummaryPanel from "./components/IssuesSummaryPanel";
import ValidationGrid from "./components/ValidationGrid";

const FILTER_ALL = "all";

const getFailedFixKeys = (errors = []) => new Set(
    errors
        .map((error) => {
            const match = error.match(/^clbId=(\d+)\s+column="([^"]+)"/);
            return match ? `${match[1]}:${match[2]}` : null;
        })
        .filter(Boolean),
);

const StepRunMappings = ({
    aiPanelOpen = false,
    columnTypeMismatch,
    columnWarnings,
    effectiveWarnings,
    fixedCells,
    fixedCountByColumn,
    issuesData,
    loadingIssues,
    mappings,
    mschFields,
    onAiPanelOpenChange,
    onBulkAcceptFixes,
    onFixCell,
    onRefreshIssues,
    onSuggestFixes,
    profileId,
    projectId,
    setFixedCells,
    totalWarnings,
    validationColumns,
    validationDgHeader,
    validationDgItems,
}) => {
    const [acceptedFixes, setAcceptedFixes] = useState(new Set());
    const [activeFilter, setActiveFilter] = useState(FILTER_ALL);
    const [aiFixes, setAiFixes] = useState(null);
    const [isRecalculating, setIsRecalculating] = useState(false);
    const [loadingAiFixes, setLoadingAiFixes] = useState(false);
    const [optimisticErrorDecrement, setOptimisticErrorDecrement] = useState(0);
    const [rejectedFixes, setRejectedFixes] = useState(new Set());
    const [savingCell, setSavingCell] = useState(false);

    useEffect(() => {
        setOptimisticErrorDecrement(0);
    }, [issuesData]);

    const summary = useMemo(() => issuesData?.summary || null, [issuesData]);
    const issueRows = useMemo(() => issuesData?.rows || [], [issuesData]);

    const issuesByClbId = useMemo(() => {
        const map = new Map();
        for (const row of issueRows) {
            map.set(row.clb_id, row.issues);
        }
        return map;
    }, [issueRows]);

    const affectedClbIds = useMemo(() => {
        if (activeFilter === FILTER_ALL) return null;
        const ids = new Set();
        for (const row of issueRows) {
            const hasMatch = row.issues.some((issue) =>
                activeFilter === "errors" ? issue.severity === "error" : issue.severity === "warning",
            );
            if (hasMatch) ids.add(row.clb_id);
        }
        return ids;
    }, [issueRows, activeFilter]);

    const filteredDgItems = useMemo(() => {
        if (!affectedClbIds) return validationDgItems;
        return validationDgItems.filter((item) => affectedClbIds.has(item.CLB_ID ?? item.clb_id));
    }, [validationDgItems, affectedClbIds]);

    const displayedTotalWarnings = summary ? summary.total_warnings : totalWarnings;
    const rawTotalErrors = summary ? summary.total_errors : 0;
    const totalErrors = Math.max(0, rawTotalErrors - optimisticErrorDecrement);
    const affectedRows = summary ? summary.affected_rows : 0;
    const byColumn = useMemo(() => summary?.by_column || {}, [summary]);

    const issueGroups = useMemo(() => {
        const errors = [];
        const warnings = [];
        for (const row of issueRows) {
            for (const issue of row.issues) {
                const target = issue.severity === "error" ? errors : warnings;
                const existing = target.find((g) => g.column === issue.column && g.type === issue.type);
                if (existing) {
                    existing.count += 1;
                } else {
                    target.push({ column: issue.column, type: issue.type, count: 1 });
                }
            }
        }
        return { errors, warnings };
    }, [issueRows]);


    const handleValidationRowEdit = useCallback((newRow, oldRow) => {
        const changedCol = validationColumns.find((col) => newRow[col] !== oldRow[col]);
        if (!changedCol) return newRow;

        const cellKey = `${newRow.uniqueId ?? newRow.CLB_ID}__${changedCol}`;
        const newValue = newRow[changedCol];
        const clbId = newRow.CLB_ID ?? newRow.clb_id;
        const bareColumn = changedCol.replace(/^CDM_[^.]+\./, "");

        if (onFixCell && profileId && projectId && clbId) {
            setSavingCell(true);
            onFixCell(profileId, projectId, clbId, bareColumn, newValue ?? null)
                .then((result) => {
                    if (result?.success && onRefreshIssues) onRefreshIssues();
                })
                .finally(() => setSavingCell(false));
        }

        const mismatch = columnTypeMismatch[changedCol];
        if (mismatch) {
            const resolved = isCellMismatchResolved(newValue, mismatch.targetType, mismatch.sourceType);
            if (resolved) {
                setFixedCells((prev) => {
                    if (prev.has(cellKey)) return prev;
                    const next = new Set(prev);
                    next.add(cellKey);
                    return next;
                });
            } else {
                setFixedCells((prev) => {
                    if (!prev.has(cellKey)) return prev;
                    const next = new Set(prev);
                    next.delete(cellKey);
                    return next;
                });
            }
        }
        return newRow;
    }, [validationColumns, columnTypeMismatch, setFixedCells, onFixCell, profileId, projectId, onRefreshIssues]);

    const handleSuggestFixes = useCallback((columnNames) => {
        if (!onSuggestFixes || !profileId || !projectId) return;
        setLoadingAiFixes(true);
        setAiFixes(null);
        setAcceptedFixes(new Set());
        setRejectedFixes(new Set());
        onSuggestFixes(profileId, projectId, columnNames)
            .then((result) => setAiFixes(result || null))
            .catch(() => setAiFixes(null))
            .finally(() => setLoadingAiFixes(false));
    }, [onSuggestFixes, profileId, projectId]);


    const handleBulkAcceptSafe = useCallback(() => {
        if (!onBulkAcceptFixes || !profileId || !projectId || !aiFixes?.suggestions) return;
        const candidates = [];
        for (const col of aiFixes.suggestions) {
            for (const fix of col.fixes || []) {
                const key = `${fix.clb_id}:${col.column_name}`;
                if (acceptedFixes.has(key) || rejectedFixes.has(key)) continue;
                if (fix.status !== "applied" || fix.requires_review) continue;
                candidates.push({ clbId: fix.clb_id, columnName: col.column_name, newValue: fix.new_value });
            }
        }
        if (!candidates.length) return;
        setOptimisticErrorDecrement((prev) => prev + candidates.length);
        setIsRecalculating(true);
        const fixes = candidates.map((c) => ({ clbId: c.clbId, column: c.columnName, newValue: c.newValue ?? null }));
        onBulkAcceptFixes(profileId, String(projectId), fixes)
            .then((result) => {
                const failedCount = result?.failed ?? candidates.length;
                const acceptedCount = result?.accepted ?? 0;
                const failedFixKeys = getFailedFixKeys(result?.errors || []);
                if (failedCount > 0) {
                    setOptimisticErrorDecrement((prev) => Math.max(0, prev - failedCount));
                }
                if (acceptedCount > 0 && acceptedCount + failedFixKeys.size === candidates.length) {
                    const acceptedKeys = candidates
                        .filter((c) => !failedFixKeys.has(`${c.clbId}:${c.columnName}`))
                        .map((c) => `${c.clbId}:${c.columnName}`);
                    setAcceptedFixes((prev) => {
                        const next = new Set(prev);
                        for (const k of acceptedKeys) next.add(k);
                        return next;
                    });
                }
                if (onRefreshIssues) onRefreshIssues();
                setIsRecalculating(false);
            })
            .catch(() => {
                setOptimisticErrorDecrement((prev) => Math.max(0, prev - candidates.length));
                setIsRecalculating(false);
            });
    }, [onBulkAcceptFixes, profileId, projectId, aiFixes, acceptedFixes, rejectedFixes, onRefreshIssues]);

    const handleBulkApproveColumn = useCallback((columnName, fixes) => {
        if (!onBulkAcceptFixes || !profileId || !projectId) return;
        const candidates = (fixes || []).filter((f) => {
            const key = `${f.clb_id}:${columnName}`;
            return f.status === "applied" && !acceptedFixes.has(key) && !rejectedFixes.has(key);
        });
        if (!candidates.length) return;
        const bulkFixes = candidates.map((f) => ({ clbId: f.clb_id, column: columnName, newValue: f.new_value ?? null }));
        onBulkAcceptFixes(profileId, String(projectId), bulkFixes)
            .then((result) => {
                const acceptedCount = result?.accepted ?? 0;
                const failedFixKeys = getFailedFixKeys(result?.errors || []);

                if (acceptedCount > 0 && acceptedCount + failedFixKeys.size === candidates.length) {
                    const acceptedKeys = candidates
                        .filter((candidate) => !failedFixKeys.has(`${candidate.clb_id}:${columnName}`))
                        .map((candidate) => `${candidate.clb_id}:${columnName}`);
                    setAcceptedFixes((prev) => {
                        const next = new Set(prev);
                        for (const key of acceptedKeys) next.add(key);
                        return next;
                    });
                }

                if (onRefreshIssues) onRefreshIssues();
            })
            .catch(() => {});
    }, [onBulkAcceptFixes, profileId, projectId, acceptedFixes, rejectedFixes, onRefreshIssues]);

    const handleRejectRule = useCallback((columnName, fixes) => {
        const nextRejectedKeys = (fixes || []).map((fix) => `${fix.clb_id}:${columnName}`);
        if (!nextRejectedKeys.length) return;
        setRejectedFixes((prev) => {
            const next = new Set(prev);
            nextRejectedKeys.forEach((key) => next.add(key));
            return next;
        });
    }, []);


    const isScanning = issuesData === null;

    const showAiPanel = onSuggestFixes && (totalErrors > 0 || displayedTotalWarnings > 0) && aiPanelOpen;

    if (isScanning) {
        return (
            <SovosBox
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    minHeight: "calc(100vh - 280px)",
                }}
            >
                <CircularProgress size={28} />
                <SovosTypography variant="body2" sx={{ color: "text.secondary" }}>
                    Scanning for data issues…
                </SovosTypography>
            </SovosBox>
        );
    }

    return (
        <SovosBox
            sx={{
                display: "grid",
                gridTemplateColumns: showAiPanel ? "minmax(0, 1fr) 395px" : "minmax(0, 1fr)",
                gap: 0,
                alignItems: "stretch",
                minHeight: "calc(100vh - 220px)",
            }}
        >
            <SovosBox sx={{ minWidth: 0 }}>
                <IssuesSummaryPanel
                    activeFilter={activeFilter}
                    affectedRows={affectedRows}
                    isRecalculating={isRecalculating}
                    isScanning={isScanning}
                    issueGroups={issueGroups}
                    issuesData={issuesData}
                    onFilterChange={setActiveFilter}
                    totalErrors={totalErrors}
                    totalWarnings={displayedTotalWarnings}
                />

                <ValidationGrid
                    byColumn={byColumn}
                    columnWarnings={columnWarnings}
                    effectiveWarnings={effectiveWarnings}
                    filteredDgItems={filteredDgItems}
                    fixedCells={fixedCells}
                    header={validationDgHeader}
                    issuesByClbId={issuesByClbId}
                    loadingIssues={loadingIssues}
                    mappings={mappings}
                    mschFields={mschFields}
                    onRowEdit={handleValidationRowEdit}
                    savingCell={savingCell}
                />
            </SovosBox>

            {showAiPanel && (
                <SovosBox sx={{ minWidth: 0, height: "100%" }}>
                    <AiFixPanel
                        acceptedFixes={acceptedFixes}
                        aiFixes={aiFixes}
                        issueSummary={summary}
                        loadingAiFixes={loadingAiFixes}
                        onBulkAcceptSafe={handleBulkAcceptSafe}
                        onBulkApproveColumn={handleBulkApproveColumn}
                        onClose={() => onAiPanelOpenChange && onAiPanelOpenChange(false)}
                        onRejectRule={handleRejectRule}
                        onRequestFixes={handleSuggestFixes}
                        rejectedFixes={rejectedFixes}
                    />
                </SovosBox>
            )}
        </SovosBox>
    );
};

export default StepRunMappings;
